import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Strikethrough,
  Underline as UnderlineIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface RichTextEditorProps {
  /** HTML string. The editor sanitises with Tiptap's allowed-schema
   *  on input — readers must still pass the rendered output through
   *  DOMPurify (see RichTextRenderer). */
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** Read-only when false: hides the toolbar and disables editing. */
  editable?: boolean;
  /** Tighter min-height for embedded contexts (e.g. contact sheet). */
  minHeight?: number;
  /** Optional inline status — "Saving…" / "Saved ✓" badge bottom-right. */
  status?: "idle" | "saving" | "saved" | "error";
  /** Move the cursor into the editor on mount. Used by the reveal
   *  pattern in NotesEditor so the user can start typing immediately
   *  after clicking "Add a note". */
  autoFocus?: boolean;
  /** Fired when the editor surface loses focus. Receives the current
   *  HTML so the parent can decide to flush its debounced save and
   *  collapse the surface back to read mode. */
  onBlur?: (html: string) => void;
}

export function RichTextEditor({
  content,
  onChange,
  placeholder = "Add notes…",
  editable = true,
  minHeight = 120,
  status = "idle",
  autoFocus = false,
  onBlur,
}: RichTextEditorProps) {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      // StarterKit ships heading / blockquote / lists / code etc. We
      // disable headings — these are short-form notes, not docs.
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        // Keep urls safe — Tiptap's default already strips javascript:
        // but we also prefix with rel=noopener for external content.
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
          class: "text-indigo-600 underline underline-offset-2",
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: content || "",
    editable,
    autofocus: autoFocus ? "end" : false,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Tiptap emits "<p></p>" for an empty doc — collapse that to ""
      // so the parent doesn't see noise as content.
      onChange(html === "<p></p>" ? "" : html);
    },
    onBlur: ({ editor }) => {
      if (!onBlur) return;
      const html = editor.getHTML();
      onBlur(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-blockquote:my-2 prose-blockquote:border-l-2 prose-blockquote:border-slate-300 prose-blockquote:pl-3 prose-blockquote:italic prose-blockquote:text-slate-600",
      },
    },
  });

  // Sync external content changes (e.g. switching companies in the
  // detail sheet) without losing focus. setContent re-emits onUpdate
  // by default — pass `false` for that flag so we don't bounce the
  // change back to the parent.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = content || "";
    if (current === incoming) return;
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [content, editor]);

  // Editable prop changes (e.g. permission revoke) flip the editor's
  // mode without remounting it.
  useEffect(() => {
    if (!editor) return;
    if (editor.isEditable !== editable) editor.setEditable(editable);
  }, [editable, editor]);

  const openLinkDialog = useCallback(() => {
    if (!editor) return;
    setLinkDialogOpen(true);
  }, [editor]);

  if (!editor) return null;

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border border-slate-200 bg-white transition-colors",
        "focus-within:border-indigo-300 focus-within:ring-1 focus-within:ring-indigo-200",
        !editable && "border-transparent bg-transparent",
      )}
    >
      {editable && (
        <div className="sticky top-0 z-10 flex items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
          <ToolbarButton
            label="Bold"
            icon={Bold}
            active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
          <ToolbarButton
            label="Italic"
            icon={Italic}
            active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
          <ToolbarButton
            label="Underline"
            icon={UnderlineIcon}
            active={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          />
          <ToolbarButton
            label="Strikethrough"
            icon={Strikethrough}
            active={editor.isActive("strike")}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          />
          <ToolbarSeparator />
          <ToolbarButton
            label="Bullet list"
            icon={List}
            active={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          />
          <ToolbarButton
            label="Numbered list"
            icon={ListOrdered}
            active={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />
          <ToolbarSeparator />
          <ToolbarButton
            label="Link"
            icon={LinkIcon}
            active={editor.isActive("link")}
            onClick={openLinkDialog}
          />
          <ToolbarButton
            label="Quote"
            icon={Quote}
            active={editor.isActive("blockquote")}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          />
        </div>
      )}

      <div className="overflow-y-auto px-4 py-3" style={{ minHeight, maxHeight: 400 }}>
        <EditorContent editor={editor} />
      </div>

      {/* Save status — "Saved ✓" fades in once a save resolves and
       *  fades out shortly after; "Saving…" is intentionally silent
       *  so the user isn't distracted while typing. Errors are loud
       *  and stay until the next successful save. */}
      <AnimatePresence>
        {editable && status === "saved" && (
          <motion.span
            key="saved"
            initial={{ opacity: 0, y: 2 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 0 }}
            transition={{ duration: 0.2 }}
            className="pointer-events-none absolute bottom-2 right-3 text-[11px] font-medium text-emerald-600"
          >
            Saved ✓
          </motion.span>
        )}
        {editable && status === "error" && (
          <motion.span
            key="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="pointer-events-none absolute bottom-2 right-3 text-[11px] font-medium text-rose-600"
          >
            Save failed
          </motion.span>
        )}
      </AnimatePresence>

      <LinkDialog
        editor={editor}
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
      />
    </div>
  );
}

interface ToolbarButtonProps {
  label: string;
  icon: typeof Bold;
  active: boolean;
  onClick: () => void;
}

function ToolbarButton({ label, icon: Icon, active, onClick }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md transition-colors",
        active
          ? "bg-indigo-100 text-indigo-700"
          : "text-slate-500 hover:bg-slate-200/60 hover:text-slate-700",
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

function ToolbarSeparator() {
  return <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden />;
}

function LinkDialog({
  editor,
  open,
  onOpenChange,
}: {
  editor: Editor;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (!open) return;
    // Prefill with the current selection's link (if any) so editing
    // an existing link drops you into the same value.
    const current = editor.getAttributes("link").href;
    setUrl(typeof current === "string" ? current : "");
  }, [open, editor]);

  function apply() {
    const trimmed = url.trim();
    if (trimmed === "") {
      editor.chain().focus().unsetLink().run();
    } else {
      // Default to https:// when the user types a bare domain so
      // links don't resolve relative to the current host.
      const normalized = /^https?:\/\//i.test(trimmed)
        ? trimmed
        : `https://${trimmed}`;
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: normalized })
        .run();
    }
    onOpenChange(false);
  }

  function remove() {
    editor.chain().focus().unsetLink().run();
    onOpenChange(false);
  }

  const hasExistingLink = editor.isActive("link");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{hasExistingLink ? "Edit link" : "Add link"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-1">
          <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            URL
          </label>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            autoFocus
            spellCheck={false}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                apply();
              }
            }}
          />
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {hasExistingLink ? (
            <Button type="button" variant="ghost" onClick={remove}>
              Remove link
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={apply}>
              {hasExistingLink ? "Update" : "Add"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
